// In-memory only: the API-auth bearer that `authLink` attaches to every request
// lives in JS heap, never localStorage. Paired with the Cognito SDK's in-memory
// Storage (see inMemoryCognitoStorage), this keeps no token at rest in the
// browser. `FederatedAuth` re-seeds this from the Cognito session on each mount,
// so a reload restores it (after the SDK's silent re-auth) rather than reading a
// persisted copy.
let jwtToken: string | null = null;

function isTokenExpired(token: string): boolean {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    // A token with no numeric `exp` has no valid expiry — treat it as expired so
    // a malformed token is never served as a live credential. (Without this,
    // `undefined * 1000` is NaN and `NaN < now` is false, i.e. "never expires".)
    if (typeof payload.exp !== 'number') {
      return true;
    }
    // JWT `exp` is epoch SECONDS per RFC 7519 — deliberately different from
    // LangHub API timestamps, which are epoch milliseconds. Do not remove *1000.
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function getJwtToken(): string | null {
  if (!jwtToken || isTokenExpired(jwtToken)) {
    return null;
  }
  return jwtToken;
}

export function setJwtToken(token: string): void {
  jwtToken = token;
}

export function removeJwtToken(): void {
  jwtToken = null;
}

// --- Saved original location ---
// The Midway OAuth flow redirects the browser away and back; we stash where the
// user was before the round-trip so FederatedAuth can restore it afterwards.
const savedOriginalUserLocationTag = 'LangHub-ORIGINAL_USER_LOCATION';
const majorVersion = 'V0';
const minorVersion = '.01';

export const savedOriginalUserLocationKey: string =
  savedOriginalUserLocationTag + '-' + majorVersion + minorVersion;

export const readFromLocal = (key: string) => {
  const data = localStorage.getItem(key);
  try {
    return JSON.parse(data as string);
  } catch {
    return null;
  }
};

export const writeToLocal = (key: string, data: unknown) => {
  localStorage.setItem(key, JSON.stringify(data));
};
